import { useMemo } from "preact/hooks";
import { useApp } from "../app-context.js";
import {
  directoryPathParts,
  moveColor,
  moveReviewId,
  shortPath,
  shortestUniquePaths,
  splitDisplayPath,
  statusLetter,
} from "../lib/view-model.js";
import { clearMoveFocus, focusMove } from "../lib/focus.js";
import { SafeText } from "./safe-text.js";
import type { FollowMove } from "../types.js";

function FileDirectory({ abbreviatedPath, fullPath }: { abbreviatedPath: string; fullPath: string }) {
  const { distinguishing, shared } = directoryPathParts(fullPath, abbreviatedPath);
  if (!shared && !distinguishing) return null;
  return (
    <>
      {shared && <span class="path-shared"><SafeText text={shared} /></span>}
      {distinguishing && <span class="path-distinguishing"><SafeText text={distinguishing} /></span>}
    </>
  );
}

export function Sidebar({ onFollowMove }: { onFollowMove: FollowMove }) {
  const { analysis, reviewed, showReviewed } = useApp();
  const { files, moves, stats } = analysis;
  const abbreviatedPaths = useMemo(() => shortestUniquePaths(files.flatMap((file) =>
    file.status === "renamed" ? [file.oldPath, file.newPath] : [file.displayPath]
  )), [files]);

  return (
    <aside class="sidebar">
      <div class="sidebar-heading">
        <span>Changed files</span>
        <b id="file-count">{stats.filesChanged}</b>
      </div>
      <nav id="file-list" aria-label="Changed files">
        {files.map((file, index) => {
          const fullLabel = file.status === "renamed"
            ? `${file.oldPath} → ${file.newPath}`
            : file.displayPath;
          const abbreviatedOldPath = abbreviatedPaths.get(file.oldPath) || file.oldPath;
          const abbreviatedNewPath = abbreviatedPaths.get(file.newPath) || file.newPath;
          const abbreviatedDisplayPath = abbreviatedPaths.get(file.displayPath) || file.displayPath;
          const oldPath = splitDisplayPath(abbreviatedOldPath);
          const newPath = splitDisplayPath(abbreviatedNewPath);
          const displayPath = splitDisplayPath(abbreviatedDisplayPath);
          const renamedFilename = oldPath.filename === newPath.filename
            ? oldPath.filename
            : `${oldPath.filename} → ${newPath.filename}`;
          const filename = file.status === "renamed" ? renamedFilename : displayPath.filename;
          const oldFullDirectory = splitDisplayPath(file.oldPath).directory;
          const newFullDirectory = splitDisplayPath(file.newPath).directory;
          return (
            <a
              class="file-link"
              href={`#file-${index}`}
              data-file-index={index}
              title={fullLabel}
              key={index}
            >
              <i class={`status-${file.status}`}>{statusLetter(file.status)}</i>
              <span class="file-link-label">
                <strong><SafeText text={filename} /></strong>
                {file.status === "renamed" ? (
                  (oldFullDirectory || newFullDirectory) && (
                    <small>
                      <FileDirectory fullPath={file.oldPath} abbreviatedPath={abbreviatedOldPath} />
                      {oldFullDirectory !== newFullDirectory && (
                        <>
                          <span class="path-arrow"> → </span>
                          <FileDirectory fullPath={file.newPath} abbreviatedPath={abbreviatedNewPath} />
                        </>
                      )}
                    </small>
                  )
                ) : (
                  splitDisplayPath(file.displayPath).directory && (
                    <small>
                      <FileDirectory fullPath={file.displayPath} abbreviatedPath={abbreviatedDisplayPath} />
                    </small>
                  )
                )}
              </span>
            </a>
          );
        })}
      </nav>
      <div class="move-legend">
        <span class="legend-title">Detected moves</span>
        <div id="move-list">
          {!moves.length && <p class="empty-moves">None in this diff</p>}
          {moves.map((move) => {
            const hidden = reviewed.has(moveReviewId(move.id)) && !showReviewed;
            const reviewedClass = reviewed.has(moveReviewId(move.id)) && showReviewed
              ? " reviewed-visible"
              : "";
            return (
              <button
                type="button"
                class={`move-link${reviewedClass}`}
                data-move-id={move.id}
                data-review-id={moveReviewId(move.id)}
                style={{ "--move-color": moveColor(move.id) }}
                hidden={hidden}
                key={move.id}
                onMouseEnter={() => focusMove(Number(move.id))}
                onMouseLeave={clearMoveFocus}
                onClick={() => onFollowMove(move.id, "target")}
              >
                <i />
                <span>
                  <strong><SafeText text={`${shortPath(move.source.path)} → ${shortPath(move.target.path)}`} /></strong>
                  <small>{move.lineCount} lines · {Math.round(move.similarity * 100)}%</small>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </aside>
  );
}
